
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import {
  Document,
  ExternalValidationResults,
} from "../../../../domain/document";
import { FormatReference } from "../../../../domain/format";
import { EditingDocument } from "../../../../components/document/editor";
import { SpecifiedPatientViewMixin } from "../../../patient/mixins";
import { default as DocumentEditorBaseMixin } from "./DocumentEditorBaseMixin.vue";
import { DocumentMerger } from "../../helpers";
import { AsyncUtility, BrowserUtility } from "../../../../utilities";
import { IConfiguration } from "../../../../domain/configuration";
import { PatientProfileApplicator } from "../../editor/helpers";

@Component({
})
export default class NewDocumentEditorBaseMixin extends Mixins(SpecifiedPatientViewMixin, DocumentEditorBaseMixin) {
  @Prop()
  defaultFormatId: number;

  @Prop()
  rootFolderId: string;

  @Prop()
  folderId: string;

  @Prop()
  defaultFormatTabName: string;

  @Prop()
  documentKindIds: number[];

  @Prop()
  formatId: number;

  @Prop()
  formatIds: number[];

  @Prop({ default: null })
  formatName: string;

  @Prop()
  chainedFormatIds: number[];

  @Prop()
  institutionNo: string;

  @Prop()
  regionalInstitutionNo: string;

  @Prop()
  hisInfoGetterParameter: string;

  @Prop()
  systemKey: string;

  @Prop()
  linkKey: string;

  @Prop()
  validateSelectedFormat: (selected?: FormatReference) => Promise<boolean>;

  @Prop()
  create: (patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string) => Promise<Document>;

  @Prop({ default: null })
  initialize: (source: Document, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string) => Promise<void>;

  @Prop({ default: true })
  isFormatIdRequired: boolean;

  @Prop()
  applyExternalRequestObjectValues: (document: EditingDocument) => Promise<void>;

  @Prop()
  validateWithExternalValidationUrl: () => Promise<ExternalValidationResults>;

  @Prop()
  validateSelectedFormatWriteMethod: (selected?: FormatReference) => boolean;

  data(): any {
    return {
      format: null as FormatReference,
      isFormatIdInvalid: false,
      isSpecifiedFormatLoading: false,
      isValidatedValidFormat: false,
    };
  }
  format: FormatReference;
  isFormatIdInvalid: boolean;
  isSpecifiedFormatLoading: boolean;
  isValidatedValidFormat: boolean;

  async initializeDocument(source: Document): Promise<void> {
    if (this.initialize) {
      await this.initialize(source, this.institutionNo, this.regionalInstitutionNo, this.hisInfoGetterParameter);
    }
  }

  get shouldSelectFormat(): boolean {
    return this.patient
      && !this.isFormatReady
      && (!this.formatId || this.isFormatIdInvalid)
      && !this.isSpecifiedFormatLoading;
  }

  isLoaded: boolean;
  shouldSelectPatient: boolean;

  get isLoading(): boolean {
    return !this.isLoaded
      && !this.shouldSelectPatient
      && !this.shouldSelectFormat;
  }

  get isFormatReady(): boolean {
    return !this.isFormatIdRequired || this.isValidFormat;
  }

  get isValidFormat(): boolean {
    return !!this.format && this.isValidatedValidFormat;
  }

  get enabledFormatId(): number {
    return !this.isFormatIdRequired
      ? this.formatId
      : this.format.id;
  }

  @Watch("format")
  onNewFormatEditorBaseFormatChanged(newValue: FormatReference, oldValue: FormatReference): void {
    if (!oldValue && newValue) {
      // ここでasyncの処理を追加すると、isFormatReadyがONになるタイミングがずれて、
      // DocumentEditorMixinでeditingSourceをWatchしているトリガが発火しなくなることがあるので注意すること
      this.validateFormat(newValue);
      if (!this.isValidFormat) { return; }
      this.formatReady(newValue);
    }
  }

  @Emit()
  formatReady(format: FormatReference): void {}

  created() {
    if (this.formatId &&  this.isFormatIdRequired) {
      this.isSpecifiedFormatLoading = true;
      const repository = DomainRegistry.format.getFormatRepository();
      repository.getFormatReference(this.formatId)
        .then(f => this.format = f)
        .catch(e => {
          this.$error(
            this.$wording.format.name + "ID不正",
            "指定された" + this.$wording.format.name + "IDに該当する" + this.$wording.format.name + "が見つかりませんでした。",
            e,
          );
          this.isFormatIdInvalid = true;
        })
        .finally(() => {
          this.isSpecifiedFormatLoading = false;
        });
    }
  }

  validateFormat(formatReference: FormatReference): void {
    this.isValidatedValidFormat = !this.validateSelectedFormatWriteMethod || this.validateSelectedFormatWriteMethod(formatReference);
  }

  isLoadRequested: boolean = false;

  loadNewDocument(): void {
    const patientNo = this.patient
      ? this.patient.id
      : this.patientNo;
    if (!patientNo || !this.isFormatReady || this.isLoadRequested) {
      return;
    }
    this.isLoadRequested = true;
    this.executeCreate(patientNo, this.enabledFormatId);
  }

  private executeCreate(patientNo: string, formatId: number): void {
    this.create(patientNo, formatId, this.institutionNo, this.regionalInstitutionNo, this.hisInfoGetterParameter, this.systemKey, this.linkKey)
    .then(d => {
      if (d) {
        d.chainedFormatOriginalIds = this.chainedFormatIds;
        d.freezeItems();
      }
      this.editingSource = d;
    })
    .catch(e => {
      this.$fatal(
        "新規作成文書読込失敗",
        "指定された患者番号(patientNo)と文書" + this.$wording.format.name + "ID(formatId)の新規作成文書読込失敗しました。",
        e
      );
    });
  }

  async mergeDocumentAndApplyPatientProfileAsync(document: Document, valueSourceDocument: Document): Promise<Document> {
    const merger = new DocumentMerger(document);
    const isByName = !merger.isSameFormat(valueSourceDocument);
    merger.mergeDocument(valueSourceDocument, isByName);
    await merger.copyImagesFromDocumentAsync();
    this.$logger.info("画像コピー完了");
    if (this.$configuration.behavior.document.editor.patientProfile.isEnable) {
      const patientProfileApplicator = new PatientProfileApplicator(merger.source);
      patientProfileApplicator.apply(merger.source.source.patientProfile);
      this.$logger.info("患者プロフィール適用完了");
    }
    if (BrowserUtility.isChromiumBrowser()) {
      if (merger.source.externalScriptRunner.hasRequestedScripts) {
        const config = this.$store.getters.configuration as IConfiguration;
        if (!config.behavior.localhost.internalScript.isEnable) {
          // ChromeはUrlSchemeを叩く間隔が短いと対象のEXEを起動できない現象が発生する。
          // 3.5秒程度の間隔を空けると起動できる模様なので、無理やり間隔を空けている。
          this.$logger.debug("ChromeのUrlScheme対策でsleepします。");
          await AsyncUtility.sleep(4000);
          this.$logger.debug("ChromeのUrlScheme対策でsleepしました。");
        }
      }
    }
    await merger.executeScriptInDocumentAtCalculateAsync(this.$configuration.behavior.document.editor.externalScript.shouldExecuteAsStackedRequest);
    const isSuccess = await merger.executeStackedScriptsAsyncWithResult();
    if (!isSuccess) {
      this.$error("外部スクリプト", "外部スクリプトの実行中にエラーが発生しています。");
    }
    return merger.sourceAsDocument;
  }
}
